import { CenterContainer } from 'components/common/CenterContainer'
import { useCollectiveDemoContext } from 'context/CollectiveDemoContext'
import { useCurrentUser } from 'hooks'
import useLocalStorage from 'hooks/useLocalStorage'
import { isValidEmail } from 'lib/helpers'
import useRecaptcha from 'hooks/useRecaptcha'
import { useRouter } from 'next/router'
import { useCallback, useMemo, useState } from 'react'
import CollectiveDetails from '../CollectiveDetails/CollectiveDetails'
import CollectiveMembers from '../CollectiveMembers'
import CollectiveProposal from '../CollectiveProposals/CollectiveProposal'
import DemoControls from '../Demo/DemoControls'
import DemoStepper from '../Demo/DemoStepper'
import { DemoSelectWallet } from './DemoSelectWallet'
import { DemoWalletConnecting } from './DemoWalletConnecting'
import BaseModal from 'components/modals/BaseModal'
import { Button, Typography } from 'components/common'
import TextField from 'components/common/TextField'

export const CollectiveDemo: React.FC = () => {
  const router = useRouter()
  const viewer = useCurrentUser()

  const { viewingMembers, viewingProposal } = useCollectiveDemoContext()
  const { demoWalletLinking, demoWalletLinked } = useCollectiveDemoContext()

  const [email, setEmail] = useState(viewer.email || '')
  const [saving, setSaving] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useLocalStorage<string | null>(
    'collectiveDemoEmail:submittedV2',
    null
  )

  const errored = useMemo(() => !email.length || !isValidEmail(email), [email])
  const emailError = useMemo(() => !isValidEmail(email), [email])

  const submitEmail = useCallback(
    async recaptchaToken => {
      setSaving(true)

      const resp = await fetch('/api/collectiveDemoContact', {
        method: 'POST',
        body: JSON.stringify({
          email,
          recaptchaToken
        })
      }).then(x => x.json())

      window.analytics.track('Collectives Submitted Demo Request')

      if (resp.success) {
        setSaving(false)
        setSubmittedEmail(email)
      } else {
        setSaving(false)
      }
    },
    [email, setSubmittedEmail]
  )

  const onEmailSubmit = useCallback(
    (recaptchaToken: string) => {
      submitEmail(recaptchaToken)
    },
    [submitEmail]
  )

  const { handleReCaptchaVerify } = useRecaptcha(
    onEmailSubmit,
    'collectiveDemoSubmit'
  )

  const showEmailForm = useMemo(() => !submittedEmail, [submittedEmail])

  return (
    <>
      {!demoWalletLinked && demoWalletLinking === 'picking' && (
        <DemoSelectWallet />
      )}

      {!demoWalletLinked && demoWalletLinking === 'connecting' && (
        <DemoWalletConnecting />
      )}

      <CenterContainer>
        <>
          <div className="sticky top-[64px] z-1000 w-full">
            <div className="max-w-[1200px] mx-auto px-2">
              <DemoControls />
            </div>
          </div>

          <div className="lg:w-[1330px] w-full mr-auto">
            <div className="grid grid-cols-12 w-full gap-2">
              <div className="col-span-12 lg:col-span-3">
                <DemoStepper
                  atEndDo={() => router.push('/collectives/launch')}
                />
              </div>

              <div className="col-span-12 lg:col-span-9 basis-full w-full flex-grow-0">
                <div className="m-2 -mb-1 rounded-lg border border-solid border-gray-300">
                  {viewingProposal && viewingProposal.id ? (
                    <CollectiveProposal proposalId={viewingProposal.id} />
                  ) : viewingMembers ? (
                    <CollectiveMembers />
                  ) : (
                    <CollectiveDetails />
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
        {showEmailForm && (
          <BaseModal>
            <div className="p-4 w-full">
              <Typography>Share your email to start the demo</Typography>

              <div className="mt-4">
                <TextField
                  label="Email Address"
                  id="email"
                  placeholder="dao@upstreamapp.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  error={emailError}
                />
              </div>

              <div className="text-center mt-2">
                <Button
                  disabled={errored}
                  label="Start Demo"
                  onClick={handleReCaptchaVerify}
                  color="blue"
                  loading={saving}
                />
              </div>
            </div>
          </BaseModal>
        )}
      </CenterContainer>
    </>
  )
}
